import { useState, useEffect, useRef } from 'react';

/**
 * Returns `value` delayed by `delay` ms, so that a burst of changes collapses
 * into a single update once the burst settles.
 *
 * - The first meaningful value passes through immediately. Holding it back would
 *   delay the initial render for everyone in order to help only the users who
 *   are actively panning.
 * - Every later change is debounced on the trailing edge.
 * - `''`, `null` and `undefined` count as "no value yet".
 *
 * @param {*} value the value to debounce
 * @param {number} delay debounce interval in milliseconds
 * @returns {*} the debounced value
 */
export const useDebouncedValue = (value, delay) => {
    const isEmpty = v => v === null || v === undefined || v === '';

    const [debouncedValue, setDebouncedValue] = useState(value);
    const settled = useRef(!isEmpty(value));

    useEffect(() => {
        if (!settled.current && !isEmpty(value)) {
            settled.current = true;
            setDebouncedValue(value);
            return;
        }

        const timeoutId = setTimeout(() => setDebouncedValue(value), delay);

        return () => clearTimeout(timeoutId);
    }, [value, delay]);

    return debouncedValue;
};
